import { useCallback, useEffect, useMemo, useState } from "react";
import { LoaderCircle } from "lucide-react";

import {
  ApiError,
  getConversations,
  getMe,
  logout as apiLogout,
} from "./api";
import ChatPanel from "./components/ChatPanel";
import ConversationList from "./components/ConversationList";
import LoginScreen from "./components/LoginScreen";
import type { Conversation, Mode, User } from "./types";


const TOKEN_KEY = "gdp-inbox-token";

export default function App() {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(TOKEN_KEY),
  );
  const [user, setUser] = useState<User | null>(null);
  const [booting, setBooting] = useState(Boolean(token));
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState<"" | Mode>("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [globalError, setGlobalError] = useState("");

  const visibleConversations = useMemo(
    () =>
      unreadOnly
        ? conversations.filter((conversation) => conversation.has_unread)
        : conversations,
    [conversations, unreadOnly],
  );

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedId) || null,
    [conversations, selectedId],
  );

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setConversations([]);
    setSelectedId(null);
    setUnreadOnly(false);
  }, []);

  useEffect(() => {
    if (!token) {
      setBooting(false);
      return;
    }
    let active = true;
    getMe(token)
      .then((result) => {
        if (active) setUser(result);
      })
      .catch((caught) => {
        if (active) {
          if (caught instanceof ApiError && caught.status === 401) {
            clearSession();
          } else {
            setGlobalError("Backend non raggiungibile. Verifica la connessione.");
          }
        }
      })
      .finally(() => {
        if (active) setBooting(false);
      });
    return () => {
      active = false;
    };
  }, [clearSession, token]);

  const loadConversations = useCallback(async (silent = false) => {
    if (!token || !user) return;
    if (!silent) setLoadingConversations(true);
    try {
      const result = await getConversations(token, search, modeFilter);
      setConversations(result.results);
      setGlobalError("");
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        clearSession();
      } else {
        setGlobalError(
          caught instanceof Error
            ? caught.message
            : "Impossibile caricare le conversazioni.",
        );
      }
    } finally {
      if (!silent) setLoadingConversations(false);
    }
  }, [clearSession, modeFilter, search, token, user]);

  useEffect(() => {
    if (!token || !user) return;
    const debounce = window.setTimeout(() => void loadConversations(), 220);
    const interval = window.setInterval(() => void loadConversations(true), 10000);
    return () => {
      window.clearTimeout(debounce);
      window.clearInterval(interval);
    };
  }, [loadConversations, token, user]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const refreshAfterPush = (event: MessageEvent) => {
      if (event.data?.type === "GDP_PUSH_RECEIVED") {
        void loadConversations(true);
      }
      if (event.data?.type === "GDP_OPEN_CONVERSATION") {
        const conversationId = Number(event.data.conversationId);
        if (!Number.isSafeInteger(conversationId) || conversationId < 1) return;

        setSearch("");
        setModeFilter("");
        setUnreadOnly(false);
        setSelectedId(conversationId);

        const url = new URL(window.location.href);
        url.searchParams.set("conversation", String(conversationId));
        window.history.replaceState(
          {},
          "",
          `${url.pathname}${url.search}${url.hash}`,
        );
      }
    };
    navigator.serviceWorker.addEventListener("message", refreshAfterPush);
    return () =>
      navigator.serviceWorker.removeEventListener("message", refreshAfterPush);
  }, [loadConversations]);

  useEffect(() => {
    const badgeNavigator = navigator as Navigator & {
      clearAppBadge?: () => Promise<void>;
    };
    const clearBadge = () => {
      if (document.visibilityState === "visible" && badgeNavigator.clearAppBadge) {
        void badgeNavigator.clearAppBadge().catch(() => undefined);
      }
    };
    clearBadge();
    document.addEventListener("visibilitychange", clearBadge);
    return () => document.removeEventListener("visibilitychange", clearBadge);
  }, []);

  useEffect(() => {
    if (!conversations.length) return;
    const requestedId = Number(
      new URL(window.location.href).searchParams.get("conversation"),
    );
    if (
      Number.isSafeInteger(requestedId) &&
      conversations.some((conversation) => conversation.id === requestedId)
    ) {
      setSelectedId(requestedId);
    }
  }, [conversations]);

  function authenticated(nextToken: string, nextUser: User) {
    localStorage.setItem(TOKEN_KEY, nextToken);
    setToken(nextToken);
    setUser(nextUser);
    setBooting(false);
  }

  async function handleLogout() {
    if (token) {
      try {
        await apiLogout(token);
      } catch {
        // Il logout locale deve funzionare anche se il backend non risponde.
      }
    }
    clearSession();
  }

  function updateConversation(updated: Conversation) {
    setConversations((current) =>
      current.map((item) => (item.id === updated.id ? updated : item)),
    );
  }

  function selectConversation(conversationId: number | null) {
    setSelectedId(conversationId);
    const url = new URL(window.location.href);
    if (conversationId) {
      url.searchParams.set("conversation", String(conversationId));
    } else {
      url.searchParams.delete("conversation");
    }
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }

  const markConversationRead = useCallback((conversationId: number) => {
    setConversations((current) =>
      current.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, has_unread: false }
          : conversation,
      ),
    );
  }, []);

  if (booting) {
    return (
      <main className="app-loading">
        <LoaderCircle size={28} className="spin" />
        <span>Apertura GDP WhatsApp…</span>
      </main>
    );
  }

  if (!token || !user) {
    return <LoginScreen onAuthenticated={authenticated} />;
  }

  return (
    <div className={`inbox-app ${selectedId ? "mobile-chat-open" : ""}`}>
      <ConversationList
        conversations={visibleConversations}
        selectedId={selectedId}
        loading={loadingConversations}
        search={search}
        modeFilter={modeFilter}
        unreadOnly={unreadOnly}
        user={user}
        token={token}
        onSearchChange={setSearch}
        onModeFilterChange={(mode) => {
          setModeFilter(mode);
          if (mode === "") setUnreadOnly(false);
        }}
        onUnreadOnlyChange={setUnreadOnly}
        onSelect={(conversation) => selectConversation(conversation.id)}
        onLogout={() => void handleLogout()}
      />
      <ChatPanel
        token={token}
        conversation={selectedConversation}
        onBack={() => selectConversation(null)}
        onConversationUpdated={updateConversation}
        onConversationRead={markConversationRead}
        onMessageSent={() => void loadConversations(true)}
      />
      {globalError && (
        <div className="global-error" role="alert">
          {globalError}
          <button onClick={() => setGlobalError("")}>×</button>
        </div>
      )}
    </div>
  );
}
